import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  BarChart2, ShoppingCart, ShoppingBag, Users, Truck,
  Package, ArrowUpRight, CreditCard, Scale, TrendingUp,
  Download, Filter, RefreshCw, Printer, FileSpreadsheet,
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle,
  TrendingDown,
} from 'lucide-react';
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Card, Breadcrumb, LoadingSpinner, Badge, Pagination, MoneyDual } from '../components/common';
import AfghanDatePicker from '../components/common/AfghanDatePicker';
import { fmtNum, isoToAfghan, todayISO, MONTHS_AF } from '../utils/dateUtils';
import { useBranch } from '../hooks/useBranch';
import api from '../services/api';
import useLang from '../i18n';

const PIE_COLORS = ['#2563eb','#16a34a','#dc2626','#d97706','#7c3aed','#0891b2','#be185d','#065f46'];
const fmt = (n) => fmtNum(Math.round(n||0));

// ── Shared Date Filter Bar ──────────────────────────────
function DateFilterBar({ startDate, endDate, period, onPeriod, onStart, onEnd, onRefresh }) {
  const { t } = useLang();
  const now = new Date();
  const presets = [
    { v:'today',   l:t('today') },
    { v:'week',    l:t('thisWeek') },
    { v:'month',   l:t('thisMonth') },
    { v:'quarter', l:t('thisQuarter') },
    { v:'year',    l:t('thisYear') },
    { v:'custom',  l:t('custom') },
  ];
  return (
    <Card className="p-4 mb-5">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs font-bold text-gray-500 mb-2">{t('timePeriod')}</p>
          <div className="flex flex-wrap gap-1.5">
            {presets.map(p => (
              <button key={p.v} onClick={() => onPeriod(p.v)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all
                  ${period===p.v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400'}`}>
                {p.l}
              </button>
            ))}
          </div>
        </div>
        {period === 'custom' && (
          <>
            <div className="min-w-[160px]">
              <AfghanDatePicker label={t('startDate')} value={startDate} onChange={onStart} />
            </div>
            <div className="min-w-[160px]">
              <AfghanDatePicker label={t('endDate')} value={endDate} onChange={onEnd} />
            </div>
          </>
        )}
        <button onClick={onRefresh} className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 text-xs">
          <RefreshCw className="w-3.5 h-3.5" /> {t('refresh')}
        </button>
      </div>
    </Card>
  );
}

// ── Print helper ────────────────────────────────────────
function printTable(title, headers, rows, printDateLabel) {
  const w = window.open('', '_blank');
  w.document.write(`<!DOCTYPE html>
  <html dir="rtl">
  <head>
    <meta charset="UTF-8">
    <title>${title}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { 
        font-family: 'Vazirmatn', 'Segoe UI', Tahoma, sans-serif; 
        font-size: 12px; 
        direction: rtl; 
        padding: 30px; 
        background: #fff; 
        color: #1e293b;
        line-height: 1.8;
      }
      h2 { 
        color: #1e40af; 
        font-size: 20px; 
        margin-bottom: 6px; 
        font-weight: 700;
      }
      .sub { 
        color: #64748b; 
        font-size: 11px; 
        margin-bottom: 20px; 
        border-bottom: 1px solid #e2e8f0; 
        padding-bottom: 12px;
      }
      table { 
        width: 100%; 
        border-collapse: collapse; 
        margin-top: 10px;
        font-size: 12px;
      }
      th { 
        background: #f1f5f9; 
        padding: 10px 12px; 
        text-align: right; 
        font-size: 11px; 
        font-weight: 700;
        color: #475569; 
        border-bottom: 2px solid #e2e8f0;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }
      td { 
        padding: 9px 12px; 
        border-bottom: 1px solid #f1f5f9; 
        font-size: 12px;
        vertical-align: middle;
      }
      tr:hover td { background: #f8fafc; }
      .total { 
        font-weight: 700; 
        background: #eff6ff !important; 
        border-top: 2px solid #bfdbfe;
        border-bottom: 2px solid #bfdbfe;
      }
      .total td { padding: 10px 12px; }
      .footer {
        margin-top: 24px;
        padding-top: 12px;
        border-top: 1px solid #e2e8f0;
        text-align: center;
        font-size: 10px;
        color: #94a3b8;
      }
      .text-left { text-align: left; }
      .text-center { text-align: center; }
      .font-bold { font-weight: 700; }
      .text-green { color: #16a34a; }
      .text-red { color: #dc2626; }
      .text-blue { color: #2563eb; }
    </style>
  </head>
  <body>
    <h2>${title}</h2>
    <div class="sub">${printDateLabel}: ${new Date().toLocaleDateString('fa-IR')}</div>
    <table>
      <thead>
        <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="footer">${title} — چاپ شده توسط سیستم حسابداری</div>
  </body>
  </html>`);
  w.document.close();
  setTimeout(() => { w.print(); w.close(); }, 500);
}
// ── Compute date range ──────────────────────────────────
function getRange(period, customStart, customEnd) {
  const now = new Date();
  const fmtDate = d => d.toISOString().split('T')[0];
  if (period==='today')   return { s:fmtDate(new Date(now.getFullYear(),now.getMonth(),now.getDate())), e:fmtDate(now) };
  if (period==='week')    return { s:fmtDate(new Date(now.getTime()-7*86400000)), e:fmtDate(now) };
  if (period==='month')   return { s:fmtDate(new Date(now.getFullYear(),now.getMonth(),1)), e:fmtDate(now) };
  if (period==='quarter') return { s:fmtDate(new Date(now.getFullYear(),Math.floor(now.getMonth()/3)*3,1)), e:fmtDate(now) };
  if (period==='year')    return { s:fmtDate(new Date(now.getFullYear(),0,1)), e:fmtDate(now) };
  return { s:customStart, e:customEnd || fmtDate(now) };
}

// ── REPORT SECTIONS ────────────────────────────────────

export default function Reports() {
  const { t, lang } = useLang();
  const SECTIONS = [
    { key:'sales',     label:t('sales'),           icon:ShoppingCart, color:'blue'   },
    { key:'purchases', label:t('purchases'),        icon:ShoppingBag,  color:'amber'  },
    { key:'customers', label:t('customers'),        icon:Users,        color:'green'  },
    { key:'inventory', label:t('inventory'),        icon:Package,      color:'purple' },
    { key:'expenses',  label:t('expenses'),         icon:ArrowUpRight, color:'red'    },
    { key:'cashbox',   label:t('cashbox'),          icon:CreditCard,   color:'teal'   },
    { key:'income',    label:t('incomeStatement'),  icon:TrendingUp,   color:'indigo' },
  ];

  const { branchId, activeBranch } = useBranch();
  const [activeSection, setActiveSection] = useState('sales');
  const [period, setPeriod]   = useState('month');
  const [startDate, setStart] = useState('');
  const [endDate, setEnd]     = useState(todayISO());
  const [refresh, setRefresh] = useState(0);
  const [reportPage, setReportPage] = useState(1);

  const range = getRange(period, startDate, endDate);
  const params = { startDate: range.s, endDate: range.e, _r: refresh, page: reportPage };

  const salesQ      = useQuery(['report-sales',     params, branchId], () => api.get('/reports/sales-report',     { params }), { enabled: activeSection==='sales' });
  const purchasesQ  = useQuery(['report-purchases', params, branchId], () => api.get('/reports/purchases-report', { params }), { enabled: activeSection==='purchases' });
  const customersQ  = useQuery(['report-customers', params, branchId], () => api.get('/reports/customers-report', { params }), { enabled: activeSection==='customers' });
  const inventoryQ  = useQuery(['report-inventory', params, branchId], () => api.get('/reports/inventory-report', { params }), { enabled: activeSection==='inventory' });
  const expensesQ   = useQuery(['report-expenses',  params, branchId], () => api.get('/reports/expenses-report',  { params }), { enabled: activeSection==='expenses' });
  const cashboxQ    = useQuery(['report-cashbox',   params, branchId], () => api.get('/reports/cashbox-report',   { params }), { enabled: activeSection==='cashbox' });
  const incomeQ     = useQuery(['report-income',    params, branchId], () => api.get('/reports/income-statement', { params }), { enabled: activeSection==='income' });

  const onRefresh = () => setRefresh(r => r+1);

  return (
    <div>
      <Breadcrumb items={[t('companyName'), t('reports')]} />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reportCenter')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isoToAfghan(range.s)} {t('toDate')} {isoToAfghan(range.e)}
            {activeBranch && <span className="mr-2 text-blue-600 font-medium">— {activeBranch.name}</span>}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-4 lg:grid-cols-8 gap-2 mb-5">
        {SECTIONS.map(s => {
          const colors = {
            blue:'border-blue-200 bg-blue-50 text-blue-700', amber:'border-amber-200 bg-amber-50 text-amber-700',
            green:'border-green-200 bg-green-50 text-green-700', orange:'border-orange-200 bg-orange-50 text-orange-700',
            purple:'border-purple-200 bg-purple-50 text-purple-700', red:'border-red-200 bg-red-50 text-red-700',
            teal:'border-teal-200 bg-teal-50 text-teal-700', indigo:'border-indigo-200 bg-indigo-50 text-indigo-700',
          };
          const isActive = activeSection === s.key;
          return (
            <button key={s.key} onClick={() => setActiveSection(s.key)}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all
                ${isActive ? colors[s.color] + ' shadow-sm' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}>
              <s.icon className="w-5 h-5" />
              <span className="text-xs font-bold">{s.label}</span>
            </button>
          );
        })}
      </div>

      <DateFilterBar period={period} startDate={startDate} endDate={endDate}
        onPeriod={setPeriod} onStart={setStart} onEnd={setEnd} onRefresh={onRefresh} />

      {activeSection === 'sales' && (
        <SalesReport data={salesQ.data?.data} loading={salesQ.isLoading} range={range} pagination={salesQ.data?.pagination} onPageChange={setReportPage} />
      )}
      {activeSection === 'purchases' && (
        <PurchasesReport data={purchasesQ.data?.data} loading={purchasesQ.isLoading} range={range} pagination={purchasesQ.data?.pagination} onPageChange={setReportPage} />
      )}
      {activeSection === 'customers' && (
        <CustomersReport data={customersQ.data?.data} loading={customersQ.isLoading} pagination={customersQ.data?.pagination} onPageChange={setReportPage} />
      )}
      {activeSection === 'inventory' && (
        <InventoryReport data={inventoryQ.data?.data} loading={inventoryQ.isLoading} pagination={inventoryQ.data?.pagination} onPageChange={setReportPage} />
      )}
      {activeSection === 'expenses' && (
        <ExpensesReport data={expensesQ.data?.data} loading={expensesQ.isLoading} range={range} pagination={expensesQ.data?.pagination} onPageChange={setReportPage} />
      )}
      {activeSection === 'cashbox' && (
        <CashboxReport data={cashboxQ.data?.data} loading={cashboxQ.isLoading} range={range} pagination={cashboxQ.data?.pagination} onPageChange={setReportPage} />
      )}
      {activeSection === 'income' && (
        <IncomeReport data={incomeQ.data?.data} loading={incomeQ.isLoading} range={range} />
      )}
    </div>
  );
}

// ── Sales Report ─────────────────────────────────────────
function SalesReport({data, loading, range, pagination, onPageChange }) {
  const { t } = useLang();
  if (loading) return <LoadingSpinner full />;
  const { sales=[], summary={}, chart=[], topCustomers=[] } = data || {};

  const handlePrint = () => {
    const rows = sales.map(s =>
      `<tr><td>${s.number}</td><td>${isoToAfghan(s.date)}</td><td>${s.customerName||t('cash')}</td><td>${s.type==='cash'?t('cash'):t('credit')}</td><td>؋${fmt(s.total)}</td><td>؋${fmt(s.paid)}</td><td>؋${fmt(s.remaining)}</td></tr>`
    ).join('') + `<tr class="total"><td colspan="4">${t('total')}</td><td>؋${fmt(summary.totalSales)}</td><td>؋${fmt(summary.totalPaid)}</td><td>؋${fmt(summary.totalRemaining)}</td></tr>`;
    printTable(t('salesReport'), [t('number'), t('date'), t('customer'), t('type'), t('total'), t('paid'), t('remaining')], rows);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: t('totalRevenue'), afn: summary.byCurrency?.AFN?.totalSales,     usd: summary.byCurrency?.USD?.totalSales,     bg: 'bg-blue-50'  },
          { l: t('paid'),         afn: summary.byCurrency?.AFN?.totalPaid,      usd: summary.byCurrency?.USD?.totalPaid,      bg: 'bg-green-50' },
          { l: t('remaining'),    afn: summary.byCurrency?.AFN?.totalRemaining, usd: summary.byCurrency?.USD?.totalRemaining, bg: 'bg-red-50'   },
        ].map(s=>(
          <Card key={s.l} className={`p-4 ${s.bg}`}>
            <p className="text-sm text-gray-500 mb-1">{s.l}</p>
            <MoneyDual afn={s.afn} usd={s.usd} size="md" />
          </Card>
        ))}
        <Card className="p-4 bg-amber-50">
          <p className="text-sm text-gray-500 mb-1">{t('invoiceCount')}</p>
          <p className="text-xl font-bold text-amber-700">{fmt(summary.count)}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-5">
          <h3 className="font-bold text-gray-900 mb-4">{t('dailyChart')}</h3>
          {chart.length===0
            ? <div className="text-center py-10 text-gray-400 text-sm">{t('noDataChart')}</div>
            : <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chart}>
                  <defs><linearGradient id="gs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                  </linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="date" tick={{fontSize:10}} tickFormatter={v=>isoToAfghan(v)} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:10}} axisLine={false} tickLine={false} tickFormatter={v=>`${(v/1000).toFixed(0)}K`}/>
                  <Tooltip formatter={v=>`؋${fmt(v)}`} labelFormatter={v=>isoToAfghan(v)}/>
                  <Area type="monotone" dataKey="total" name={t('sales')} stroke="#2563eb" fill="url(#gs)" strokeWidth={2.5}/>
                </AreaChart>
              </ResponsiveContainer>
          }
        </Card>
        <Card className="p-5">
          <h3 className="font-bold text-gray-900 mb-4">{t('topCustomers2')}</h3>
          {topCustomers.slice(0,7).map((c,i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{i+1}</span>
                <span className="text-sm font-semibold text-gray-800 truncate max-w-[100px]">{c.name}</span>
              </div>
              <span className="text-sm font-bold text-blue-700 flex-shrink-0">؋{fmt(c.total)}</span>
            </div>
          ))}
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{t('salesList')} ({sales.length})</h3>
          <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-xs text-gray-600">
            <Printer className="w-3.5 h-3.5"/> {t('print')}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b">
              {[t('number'), t('date'), t('customer'), t('type'), t('total'), t('paid'), t('remaining'), t('status')].map(h=>(
                <th key={h} className="px-4 py-3 text-right text-xs font-bold text-gray-500 whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {sales.length===0
                ? <tr><td colSpan={8} className="text-center py-10 text-gray-400">{t('noData')}</td></tr>
                : sales.map((s,i)=>(
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs font-mono font-bold text-blue-600">{s.number}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{isoToAfghan(s.date)}</td>
                    <td className="px-4 py-3 text-sm font-semibold">{s.customerName||s.customerId?.name||t('cash')}</td>
                    <td className="px-4 py-3"><Badge label={s.type==='cash'?t('cash'):t('credit')} color={s.type==='cash'?'green':'amber'}/></td>
                    <td className="px-4 py-3 text-sm font-bold">؋{fmt(s.total)}</td>
                    <td className="px-4 py-3 text-sm text-green-600 font-semibold">؋{fmt(s.paid||0)}</td>
                    <td className="px-4 py-3 text-sm">
                      {(s.remaining||0)>0 ? <span className="text-red-600 font-bold">؋{fmt(s.remaining)}</span> : <span className="text-green-600 text-xs">✓</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${(s.remaining||0)<=0?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>
                        {(s.remaining||0)<=0?t('settled'):t('overdue2')}
                      </span>
                    </td>
                  </tr>
                ))
              }
            </tbody>
            {sales.length>0 && (
              <tfoot className="bg-blue-50 border-t-2 border-blue-200">
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-sm font-bold text-blue-800">{t('total')}</td>
                  <td className="px-4 py-3 text-sm font-bold text-blue-800">؋{fmt(summary.totalSales)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-green-700">؋{fmt(summary.totalPaid)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-red-700">؋{fmt(summary.totalRemaining)}</td>
                  <td/>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {pagination && <Pagination pagination={pagination} onChange={onPageChange} />}
      </Card>
    </div>
  );
}

// ── Purchases Report ──────────────────────────────────────
function PurchasesReport({data, loading, range, pagination, onPageChange }) {
  const { t } = useLang();
  if (loading) return <LoadingSpinner full />;
  const { purchases=[], summary={}, chart=[] } = data || {};

  const handlePrint = () => {
    const rows = purchases.map(p =>
      `<tr><td>${p.number}</td><td>${isoToAfghan(p.date)}</td><td>${p.supplierName||p.supplierId?.name||'—'}</td><td>${p.type==='cash'?t('cash'):t('credit')}</td><td>؋${fmt(p.total)}</td><td>؋${fmt(p.paid||0)}</td><td>؋${fmt(p.remaining||0)}</td></tr>`
    ).join('');
    printTable(t('purchasesReport'), [t('number'), t('date'), t('supplier'), t('type'), t('total'), t('paid'), t('remaining')], rows);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: t('purchases'), afn: summary.byCurrency?.AFN?.total,     usd: summary.byCurrency?.USD?.total,     bg: 'bg-amber-50' },
          { l: t('paid'),      afn: summary.byCurrency?.AFN?.paid,      usd: summary.byCurrency?.USD?.paid,      bg: 'bg-green-50' },
          { l: t('balance'),   afn: summary.byCurrency?.AFN?.remaining, usd: summary.byCurrency?.USD?.remaining, bg: 'bg-red-50' },
        ].map(s=>(
          <Card key={s.l} className={`p-4 ${s.bg}`}>
            <p className="text-sm text-gray-500 mb-1">{s.l}</p>
            <MoneyDual afn={s.afn} usd={s.usd} size="md" />
          </Card>
        ))}
        <Card className="p-4 bg-blue-50">
          <p className="text-sm text-gray-500 mb-1">{t('purchaseCount')}</p>
          <p className="text-xl font-bold text-blue-700">{fmt(summary.count)}</p>
        </Card>
      </div>
      <Card className="p-5">
        <h3 className="font-bold text-gray-900 mb-4">{t('dailyPurchasesChart')}</h3>
        {chart.length===0
          ? <div className="text-center py-10 text-gray-400 text-sm">{t('noDataChart')}</div>
          : <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="date" tick={{fontSize:10}} tickFormatter={v=>isoToAfghan(v)} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:10}} axisLine={false} tickLine={false} tickFormatter={v=>`${(v/1000).toFixed(0)}K`}/>
                <Tooltip formatter={v=>`؋${fmt(v)}`} labelFormatter={v=>isoToAfghan(v)}/>
                <Bar dataKey="total" name={t('purchases')} fill="#d97706" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
        }
      </Card>
      <Card>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{t('purchasesList')} ({purchases.length})</h3>
          <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-xs text-gray-600">
            <Printer className="w-3.5 h-3.5"/> {t('print')}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b">
              {[t('number'), t('date'), t('supplier'), t('type'), t('total'), t('paid'), t('remaining')].map(h=>(
                <th key={h} className="px-4 py-3 text-right text-xs font-bold text-gray-500 whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {purchases.length===0
                ? <tr><td colSpan={7} className="text-center py-10 text-gray-400">{t('noData')}</td></tr>
                : purchases.map((p,i)=>(
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs font-mono font-bold text-amber-600">{p.number}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{isoToAfghan(p.date)}</td>
                    <td className="px-4 py-3 text-sm font-semibold">{p.supplierName||p.supplierId?.name||'—'}</td>
                    <td className="px-4 py-3"><Badge label={p.type==='cash'?t('cash'):t('credit')} color={p.type==='cash'?'green':'amber'}/></td>
                    <td className="px-4 py-3 text-sm font-bold">؋{fmt(p.total)}</td>
                    <td className="px-4 py-3 text-sm text-green-600">؋{fmt(p.paid||0)}</td>
                    <td className="px-4 py-3 text-sm">
                      {(p.remaining||0)>0 ? <span className="text-red-600 font-bold">؋{fmt(p.remaining)}</span> : <span className="text-green-600 text-xs">✓</span>}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Customers Report ──────────────────────────────────────
function CustomersReport({data, loading, pagination, onPageChange }) {
  const { t } = useLang();
  if (loading) return <LoadingSpinner full />;
  const { customers=[], summary={} } = data || {};

  const handlePrint = () => {
    const rows = customers.map(c =>
      `<tr><td>${c.name}</td><td>${c.phone||'—'}</td><td>${c.type==='company'?t('company'):t('individual')}</td><td>؋${fmt(c.balance||0)}</td></tr>`
    ).join('');
    printTable(t('customersReport'), [t('name'), t('phone'), t('type'), t('balance')], rows);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        {[
          { l: t('totalCustomers'), v: summary.total||0, c: 'text-blue-700', bg: 'bg-blue-50' },
          { l: t('debtorCustomers'), v: summary.withDebt||0, c: 'text-red-700', bg: 'bg-red-50' },
          { l: t('balance'), v: `؋${fmt(summary.totalDebt)}`, c: 'text-amber-700', bg: 'bg-amber-50' },
        ].map(s=>(
          <Card key={s.l} className={`p-4 ${s.bg}`}><p className="text-sm text-gray-500 mb-1">{s.l}</p><p className={`text-xl font-bold ${s.c}`}>{s.v}</p></Card>
        ))}
      </div>
      <Card>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{t('customersList')} ({customers.length})</h3>
          <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-xs text-gray-600">
            <Printer className="w-3.5 h-3.5"/> {t('print')}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b">
              {[t('name'), t('phone'), t('type'), t('currency'), t('balance'), t('status')].map(h=>(
                <th key={h} className="px-4 py-3 text-right text-xs font-bold text-gray-500">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {customers.length===0
                ? <tr><td colSpan={6} className="text-center py-10 text-gray-400">{t('noData')}</td></tr>
                : customers.map((c,i)=>(
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-bold text-gray-800">{c.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{c.phone||'—'}</td>
                    <td className="px-4 py-3"><Badge label={c.type==='company'?t('company'):t('individual')} color="blue"/></td>
                    <td className="px-4 py-3 text-sm text-gray-500">{c.currency||'AFN'}</td>
                    <td className="px-4 py-3 text-sm">
                      {(c.balance||0)>0 ? <span className="font-bold text-red-600">؋{fmt(c.balance)}</span> : <span className="text-green-600 text-xs">{t('settled2')}</span>}
                    </td>
                    <td className="px-4 py-3"><Badge label={c.isActive!==false?t('active'):t('inactive')} color={c.isActive!==false?'green':'gray'} dot/></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
        {pagination && <Pagination pagination={pagination} onChange={onPageChange} />}
      </Card>
    </div>
  );
}

// ── Suppliers Report ──────────────────────────────────────
function SuppliersReport({data, loading }) {
  const { t } = useLang();
  if (loading) return <LoadingSpinner full />;
  const { suppliers=[], summary={} } = data || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        {[
          { l: t('totalSuppliers'), v: summary.total||0, c: 'text-amber-700', bg: 'bg-amber-50' },
          { l: t('suppliersWithDebt'), v: summary.withDebt||0, c: 'text-red-700', bg: 'bg-red-50' },
          { l: t('balance'), v: `؋${fmt(summary.totalDebt)}`, c: 'text-blue-700', bg: 'bg-blue-50' },
        ].map(s=>(
          <Card key={s.l} className={`p-4 ${s.bg}`}><p className="text-sm text-gray-500 mb-1">{s.l}</p><p className={`text-xl font-bold ${s.c}`}>{s.v}</p></Card>
        ))}
      </div>
      <Card>
        <div className="px-5 py-4 border-b border-gray-100"><h3 className="font-bold text-gray-900">{t('suppliersList')} ({suppliers.length})</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b">
              {[t('name'), t('phone'), t('type'), t('balance'), t('status')].map(h=>(
                <th key={h} className="px-4 py-3 text-right text-xs font-bold text-gray-500">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {suppliers.map((s,i)=>(
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-bold">{s.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{s.phone||'—'}</td>
                  <td className="px-4 py-3"><Badge label={s.type==='company'?t('company'):t('individual')} color="amber"/></td>
                  <td className="px-4 py-3 text-sm">
                    {(s.balance||0)>0 ? <span className="font-bold text-red-600">؋{fmt(s.balance)}</span> : <span className="text-green-600 text-xs">{t('settled2')}</span>}
                  </td>
                  <td className="px-4 py-3"><Badge label={s.isActive!==false?t('active'):t('inactive')} color={s.isActive!==false?'green':'gray'} dot/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Inventory Report ──────────────────────────────────────
function InventoryReport({data, loading, pagination, onPageChange }) {
  const { t } = useLang();
  if (loading) return <LoadingSpinner full />;
  const { products=[], summary={} } = data || {};

  const handlePrint = async () => {
    const res = await api.get('/reports/inventory-report', { params: { page: 1, limit: 9999 } });
    console.log('API Response:', res.data); // ← این را ببین در console
    const allProducts = res.data?.products || [];
    console.log('Products:', allProducts.length); // ← چند تا برگشت؟
    const rows = allProducts.map(p =>
      `<tr><td>${p.code||'—'}</td><td>${p.name}</td><td>${p.category||'—'}</td><td>${p.unit}</td><td>${fmt(p.currentStock)}</td><td>${fmt(p.minStock)}</td><td>؋${fmt(p.buyPrice)}</td><td>؋${fmt(p.currentStock*(p.buyPrice||0))}</td></tr>`
    ).join('');
    printTable(t('inventoryReport'), [t('code'), t('product'), t('category'), t('unit'), t('currentStock'), t('minStock'), t('buyPrice'), t('stockValue')], rows);
  };

  const catData = (summary.categories || []).map(c => ({
    name: c._id || c.name || '—',
    value: c.value || 0,
  }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: t('totalProducts'), v: summary.totalProducts||0, c: 'text-blue-700', bg: 'bg-blue-50' },
        ].map(s=>(
          <Card key={s.l} className={`p-4 ${s.bg}`}>
            <p className="text-sm text-gray-500 mb-1">{s.l}</p>
            <p className={`text-xl font-bold ${s.c}`}>{s.v}</p>
          </Card>
        ))}
        <Card className="p-4 bg-green-50">
          <p className="text-sm text-gray-500 mb-1">{t('stockValue')}</p>
          <MoneyDual afn={summary.byCurrency?.AFN?.totalValue} usd={summary.byCurrency?.USD?.totalValue} size="md" />
        </Card>
        {[
          { l: t('lowStockAlert'), v: `${summary.lowStock||0} ${t('products')}`, c: 'text-amber-700', bg: 'bg-amber-50' },
          { l: t('outOfStock'), v: `${summary.outOfStock||0} ${t('products')}`, c: 'text-red-700', bg: 'bg-red-50' },
        ].map(s=>(
          <Card key={s.l} className={`p-4 ${s.bg}`}><p className="text-sm text-gray-500 mb-1">{s.l}</p><p className={`text-xl font-bold ${s.c}`}>{s.v}</p></Card>
        ))}
      </div>

      {catData.length > 0 && (
        <Card className="p-5">
          <h3 className="font-bold text-gray-900 mb-4">{t('stockByCategory')}</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={catData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`}>
                  {catData.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                </Pie>
                <Tooltip formatter={v=>`؋${fmt(v)}`}/>
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {catData.map((c,i)=>(
                <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{background:PIE_COLORS[i%PIE_COLORS.length]}}/>
                    <span className="text-sm font-semibold">{c.name}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-700">؋{fmt(c.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-bold text-gray-900">{t('productsList')} ({products.length})</h3>
          <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-xs text-gray-600">
            <Printer className="w-3.5 h-3.5"/> {t('print')}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b">
              {[t('product'), t('category'), t('unit'), t('currentStock'), t('minStock'), t('buyPrice'), t('stockValue'), t('status')].map(h=>(
                <th key={h} className="px-4 py-3 text-right text-xs font-bold text-gray-500 whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {products.map((p,i)=>{
                const status = p.currentStock<=0 ? t('outOfStock') : p.currentStock<=p.minStock ? t('lowStock') : t('inStock');
                const sColor = p.currentStock<=0 ? 'bg-red-100 text-red-700' : p.currentStock<=p.minStock ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
                return (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-semibold">{p.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{p.category||'—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{p.unit}</td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-800">{fmt(p.currentStock)}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{fmt(p.minStock)}</td>
                    <td className="px-4 py-3 text-sm">{p.currency==='USD'?'$':'؋'}{fmt(p.buyPrice)}</td>
                    <td className="px-4 py-3 text-sm font-bold text-blue-700">{p.currency==='USD'?'$':'؋'}{fmt(p.currentStock*(p.buyPrice||0))}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${sColor}`}>{status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagination && <Pagination pagination={pagination} onChange={onPageChange} />}
      </Card>
    </div>
  );
}

// ── Expenses Report ───────────────────────────────────────
function ExpensesReport({data, loading, range, pagination, onPageChange }) {
  const { t } = useLang();
  if (loading) return <LoadingSpinner full />;
  const { expenses=[], byCategory=[], total=0 } = data || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-4 bg-red-50"><p className="text-sm text-gray-500">{t('totalExpenses')}</p><p className="text-2xl font-bold text-red-600 mt-1">؋{fmt(total)}</p></Card>
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-bold text-gray-900 mb-4">{t('byCategory')}</h3>
          {byCategory.length===0
            ? <p className="text-sm text-gray-400 text-center py-6">{t('noData')}</p>
            : byCategory.map((c,i)=>(
              <div key={i} className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{background:PIE_COLORS[i%PIE_COLORS.length]}}/>
                  <span className="text-sm font-semibold truncate">{c._id||t('other')}</span>
                  <span className="text-xs text-gray-400">({c.count} {t('items')})</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-red-500" style={{width:`${Math.min(100,(c.total/total)*100)}%`}}/>
                  </div>
                  <span className="text-sm font-bold text-red-600 w-24 text-left">؋{fmt(c.total)}</span>
                </div>
              </div>
            ))
          }
        </Card>
      </div>
      <Card>
        <div className="px-5 py-4 border-b"><h3 className="font-bold text-gray-900">{t('expensesList')} ({expenses.length})</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b">
              {[t('number'), t('date'), t('category'), t('description'), t('amount'), t('status')].map(h=>(
                <th key={h} className="px-4 py-3 text-right text-xs font-bold text-gray-500">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {expenses.length===0
                ? <tr><td colSpan={6} className="text-center py-10 text-gray-400">{t('noData')}</td></tr>
                : expenses.map((e,i)=>(
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs font-mono text-red-600">{e.number||'—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{isoToAfghan(e.date)}</td>
                    <td className="px-4 py-3"><Badge label={e.category||t('other')} color="red"/></td>
                    <td className="px-4 py-3 text-sm">{e.description}</td>
                    <td className="px-4 py-3 text-sm font-bold text-red-600">؋{fmt(e.amount)}</td>
                    <td className="px-4 py-3"><Badge label={e.status==='approved'?t('approved'):t('pending')} color={e.status==='approved'?'green':'amber'}/></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Cashbox Report ────────────────────────────────────────
function CashboxReport({data, loading, range, pagination, onPageChange }) {
  const { t } = useLang();
  if (loading) return <LoadingSpinner full />;
  const { transactions=[], income=0, outcome=0, balance=0 } = data || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        {[
          { l: t('totalReceipt'), v: `؋${fmt(income)}`, c: 'text-green-700', bg: 'bg-green-50' },
          { l: t('totalPayment'), v: `؋${fmt(outcome)}`, c: 'text-red-700', bg: 'bg-red-50' },
          { l: t('cashboxBalance'), v: `؋${fmt(balance)}`, c: balance>=0?'text-blue-700':'text-red-700', bg: balance>=0?'bg-blue-50':'bg-red-50' },
        ].map(s=>(
          <Card key={s.l} className={`p-4 ${s.bg}`}><p className="text-sm text-gray-500 mb-1">{s.l}</p><p className={`text-xl font-bold ${s.c}`}>{s.v}</p></Card>
        ))}
      </div>
      <Card>
        <div className="px-5 py-4 border-b"><h3 className="font-bold text-gray-900">{t('cashboxTransactions')} ({transactions.length})</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b">
              {[t('date'), t('type'), t('description'), t('amount')].map(h=>(
                <th key={h} className="px-4 py-3 text-right text-xs font-bold text-gray-500">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {transactions.map((tItem,i)=>(
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{isoToAfghan(tItem.date)}</td>
                  <td className="px-4 py-3">
                    <Badge label={tItem.type==='receipt'?t('income'):tItem.type==='payment'?t('payment'):t('transfer')} color={tItem.type==='receipt'?'green':tItem.type==='payment'?'red':'blue'}/>
                  </td>
                  <td className="px-4 py-3 text-sm">{tItem.description||'—'}</td>
                  <td className="px-4 py-3 text-sm font-bold">
                    <span className={tItem.type==='receipt'?'text-green-600':'text-red-600'}>
                      {tItem.type==='receipt'?'+':'-'}؋{fmt(tItem.amount)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && <Pagination pagination={pagination} onChange={onPageChange} />}
      </Card>
    </div>
  );
}

// ── Income Statement Report ───────────────────────────────
function IncomeReport({data, loading, range }) {
  const { t } = useLang();
  if (loading) return <LoadingSpinner full />;
  const { revenues=[], expenses=[], totalRevenue=0, cogs=0, grossProfit=0, operatingExpenses=0, totalExpenses=0, netProfit=0 } = data || {};

  const handlePrint = () => {
    const rows = [
      ...revenues.map(r=>`<tr style="background:#f0fdf4"><td><strong>${r.name}</strong></td><td style="color:green;font-weight:bold">؋${fmt(r.amount)}</td></tr>`),
      `<tr style="background:#dcfce7"><td><strong>${t('totalRevenue')}</strong></td><td style="color:green;font-weight:bold">؋${fmt(totalRevenue)}</td></tr>`,
      ...expenses.map(e=>`<tr style="background:#fef2f2"><td>${e.name}</td><td style="color:red">؋${fmt(e.amount)}</td></tr>`),
      `<tr style="background:#fee2e2"><td><strong>${t('totalExpenses')}</strong></td><td style="color:red;font-weight:bold">؋${fmt(totalExpenses)}</td></tr>`,
      `<tr style="background:#eff6ff;font-size:15px"><td><strong>${netProfit>=0?t('netProfit'):t('netProfit')}</strong></td><td style="color:${netProfit>=0?'green':'red'};font-weight:bold">؋${fmt(Math.abs(netProfit))}</td></tr>`,
    ].join('');
    printTable(t('incomeStatement2'), [t('description'), `${t('amount')} (؋)`], rows);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        {[
          { l: t('totalRevenue'), v: `؋${fmt(totalRevenue)}`, c: 'text-green-700', bg: 'bg-green-50' },
          { l: t('totalExpenses'), v: `؋${fmt(totalExpenses)}`, c: 'text-red-700', bg: 'bg-red-50' },
          { l: netProfit>=0?t('netProfit'):t('netLoss'), v: `${netProfit>=0?'+':'-'}؋${fmt(Math.abs(netProfit))}`, c: netProfit>=0?'text-blue-700':'text-red-700', bg: netProfit>=0?'bg-blue-50':'bg-red-50' },
        ].map(s=>(
          <Card key={s.l} className={`p-4 ${s.bg}`}><p className="text-sm text-gray-500 mb-1">{s.l}</p><p className={`text-xl font-bold ${s.c}`}>{s.v}</p></Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">{t('incomeStatement2')}</h3>
            <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-xs text-gray-600">
              <Printer className="w-3.5 h-3.5"/> {t('print')}
            </button>
          </div>
          <div className="bg-green-50 rounded-xl p-4 mb-3">
            <p className="text-xs font-bold text-green-700 mb-2">{t('incomeChart')}</p>
            {revenues.map((r,i)=>(
              <div key={i} className="flex justify-between py-1.5 border-b border-green-100 last:border-0">
                <span className="text-sm">{r.name}</span>
                <span className="text-sm font-bold text-green-700">؋{fmt(r.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 border-t border-green-200 mt-1">
              <span className="text-sm font-bold text-green-800">{t('totalRevenue')}</span>
              <span className="text-sm font-bold text-green-800">؋{fmt(totalRevenue)}</span>
            </div>
          </div>
          <div className="bg-red-50 rounded-xl p-4 mb-3">
            <p className="text-xs font-bold text-red-700 mb-2">{t('expensesChart')}</p>
            {expenses.map((e,i)=>(
              <div key={i} className="flex justify-between py-1.5 border-b border-red-100 last:border-0">
                <span className="text-sm">{e.name}</span>
                <span className="text-sm font-bold text-red-600">؋{fmt(e.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 border-t border-red-200 mt-1">
              <span className="text-sm font-bold text-red-800">{t('totalExpenses')}</span>
              <span className="text-sm font-bold text-red-800">؋{fmt(totalExpenses)}</span>
            </div>
          </div>
          <div className={`rounded-xl p-4 ${netProfit>=0?'bg-blue-50':'bg-red-50'}`}>
            <div className="flex justify-between">
              <span className={`font-bold ${netProfit>=0?'text-blue-800':'text-red-800'}`}>
                {netProfit>=0?t('netProfit'):t('netLoss')}
              </span>
              <span className={`text-lg font-bold ${netProfit>=0?'text-blue-700':'text-red-700'}`}>
                {netProfit>=0?'+':'-'}؋{fmt(Math.abs(netProfit))}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-bold text-gray-900 mb-4">{t('compareChart')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={[
              { name: t('incomeChart'), amount: totalRevenue, fill: '#22c55e' },
              { name: t('expensesChart'), amount: totalExpenses, fill: '#ef4444' },
              { name: netProfit>=0?t('netProfit'):t('netLoss'), amount: Math.abs(netProfit), fill: netProfit>=0?'#2563eb':'#dc2626' },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="name" tick={{fontSize:12}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:10}} axisLine={false} tickLine={false} tickFormatter={v=>`${(v/1000).toFixed(0)}K`}/>
              <Tooltip formatter={v=>`؋${fmt(v)}`}/>
              <Bar dataKey="amount" radius={[8,8,0,0]}>
                {[{fill:'#22c55e'},{fill:'#ef4444'},{fill:netProfit>=0?'#2563eb':'#dc2626'}].map((e,i)=>(
                  <Cell key={i} fill={e.fill}/>
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}